import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Controller, useForm, useWatch } from "react-hook-form";
import { Button, Grid, TextField, MenuItem } from "@mui/material";
import AppForm from "components/Shared/AppForm";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import AppSwitch from "components/Shared/AppSwitch";
import { useAuth } from "hooks/useAuth";
import { useGetDDLAccountListQuery } from "store/services/accountApi";
import { useLazyGetDDLEntityListQuery } from "store/services/entityApi";
import { useLazyGetDDLVehicleListQuery } from "store/services/vehicleApi";
import { useVehicleDetailContext } from "contexts/VehicleDetailContext";

const INITIAL_STATE = {
  trackingId: "",
  account: "",
  entity: "",
  vehicle_id: "",
  timestamp: new Date().toISOString().slice(0, 16),
  location: { lat: "", lon: "" },
  status: "",
  isActive: true,
};

const RealTimeTrackingForm = ({
  isBusy = false,
  trackingDetail = null,
  onSubmit = () => {},
}) => {
  const navigate = useNavigate();
  const { viewEntity, isAccountAdmin, isSystemAdmin } = useAuth();

  // Safely access VehicleDetailContext
  let vehicleDetail = null;
  try {
    const vehicleDetailContext = useVehicleDetailContext();
    vehicleDetail = vehicleDetailContext ? vehicleDetailContext.vehicleDetail : null;
  } catch (error) {
    console.warn("VehicleDetailContext not provided, using dropdown for vehicle selection.");
  }

  // React Hook Form
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      ...INITIAL_STATE,
      entity: viewEntity?.entity,
      account: viewEntity?.entity?.account,
      vehicle_id: vehicleDetail
        ? {
            _id: vehicleDetail._id,
            vehicle_id: vehicleDetail.vehicle_id,
            registration_number: vehicleDetail.registration_number,
          }
        : "",
    },
  });

  const _watchAccount = useWatch({ control, name: "account" });
  const _watchEntity = useWatch({ control, name: "entity" });

  // Queries
  const { data: accounts, isLoading: loadingAccounts } = useGetDDLAccountListQuery();
  const [fetchEntities, { data: entities, isLoading: loadingEntities }] =
    useLazyGetDDLEntityListQuery();
  const [fetchVehicles, { data: vehicles, isLoading: loadingVehicles }] =
    useLazyGetDDLVehicleListQuery();

  // Fetch Entities when Account changes
  useEffect(() => {
    if (isAccountAdmin || isSystemAdmin) {
      fetchEntities({
        account: _watchAccount?._id ?? viewEntity?.entity?.account?._id,
      });
    }
  }, [_watchAccount, fetchEntities, isAccountAdmin, isSystemAdmin, viewEntity]);

  // Fetch Vehicles when Entity changes
  useEffect(() => {
    if (!_watchEntity?._id || vehicleDetail) return;
    fetchVehicles({ account: _watchAccount?._id, entity: _watchEntity?._id });
  }, [_watchAccount, _watchEntity, fetchVehicles, vehicleDetail]);

  // Pre-populate vehicle_id when vehicleDetail is available
  useEffect(() => {
    if (vehicleDetail) {
      setValue("vehicle_id", {
        _id: vehicleDetail._id,
        vehicle_id: vehicleDetail.vehicle_id,
        registration_number: vehicleDetail.registration_number,
      });
    }
  }, [vehicleDetail, setValue]);

  // Reset form when trackingDetail changes
  useEffect(() => {
    if (trackingDetail) {
      reset({
        ...INITIAL_STATE,
        ...trackingDetail,
        timestamp: trackingDetail.timestamp
          ? new Date(trackingDetail.timestamp).toISOString().slice(0, 16)
          : INITIAL_STATE.timestamp,
        vehicle_id: trackingDetail.vehicle_id
          ? {
              _id: trackingDetail.vehicle_id._id,
              vehicle_id: trackingDetail.vehicle_id.vehicle_id,
              registration_number: trackingDetail.vehicle_id.registration_number,
            }
          : "",
      });
    }
  }, [trackingDetail, reset]);

  // Handle Form Submission
  const handleOnSubmit = (data) => {
    const formattedData = {
      ...data,
      account: data?.account?._id,
      entity: data?.entity?._id,
      vehicle_id: vehicleDetail?._id || data?.vehicle_id?._id, // Use vehicleDetail if available, else use form's vehicle_id
      timestamp: new Date(data.timestamp).toISOString(),
    };
    onSubmit(formattedData);
  };

  return (
    <AppForm loading={isBusy}>
      <AppForm.Content>
        <Grid container spacing={4}>
          {/* Account Selection */}
          {(isAccountAdmin || isSystemAdmin) && (
            <Grid item md={6}>
              <Controller
                name="account"
                control={control}
                rules={{ required: "Please select an account." }}
                render={({ field }) => (
                  <BaseAutocomplete
                    {...field}
                    fullWidth
                    label="Account"
                    required
                    value={field.value ?? ""}
                    onChange={(_, data) => {
                      field.onChange(data);
                      setValue("entity", "");
                      setValue("vehicle_id", "");
                    }}
                    getOptionLabel={(option) => option?.name ?? ""}
                    isOptionEqualToValue={(option, value) => option?._id === value?._id}
                    options={accounts || []}
                    disabled={isAccountAdmin}
                    loading={loadingAccounts}
                    helperText={errors?.account?.message}
                    error={!!errors?.account}
                  />
                )}
              />
            </Grid>
          )}

          {/* Entity Selection */}
          {(isAccountAdmin || isSystemAdmin) && (
            <Grid item md={6}>
              <Controller
                name="entity"
                control={control}
                rules={{ required: "Please select an entity." }}
                render={({ field }) => (
                  <BaseAutocomplete
                    {...field}
                    fullWidth
                    label="Entity"
                    required
                    value={field.value ?? ""}
                    onChange={(_, data) => {
                      field.onChange(data);
                      if (!vehicleDetail) setValue("vehicle_id", "");
                    }}
                    getOptionLabel={(option) => option?.name ?? ""}
                    isOptionEqualToValue={(option, value) => option?._id === value?._id}
                    options={entities || []}
                    loading={loadingEntities}
                    helperText={errors?.entity?.message}
                    error={!!errors?.entity}
                  />
                )}
              />
            </Grid>
          )}

          {/* Vehicle ID */}
          <Grid item md={6}>
            <Controller
              name="vehicle_id"
              control={control}
              rules={{ required: "Please select a vehicle." }}
              render={({ field: { value, onChange } }) => (
                <BaseAutocomplete
                  fullWidth
                  label="Vehicle ID"
                  required
                  value={vehicles?.find((vec) => vec._id === value?._id) ?? value ?? null}
                  onChange={(_, data) => onChange(data)}
                  getOptionLabel={(option) =>
                    option ? `${option.vehicle_id} - ${option.registration_number}` : ""
                  }
                  isOptionEqualToValue={(option, value) => option?._id === value?._id}
                  options={vehicles || []}
                  loading={loadingVehicles}
                  loadingText="Loading Vehicles..."
                  helperText={errors?.vehicle_id?.message}
                  error={!!errors?.vehicle_id}
                  disabled={!!vehicleDetail} // Disable if vehicleDetail is provided
                />
              )}
            />
          </Grid>

          {/* Timestamp */}
          <Grid item xs={12} sm={6}>
            <Controller
              name="timestamp"
              control={control}
              rules={{ required: "Timestamp is required." }}
              render={({ field }) => (
                <TextField
                  {...field}
                  required
                  fullWidth
                  label="Timestamp"
                  size="small"
                  type="datetime-local"
                  InputLabelProps={{ shrink: true }}
                  value={field.value ? field.value.slice(0, 16) : ""}
                  error={!!errors.timestamp}
                  helperText={errors.timestamp?.message}
                />
              )}
            />
          </Grid>

          {/* Location - Latitude */}
          <Grid item xs={12} sm={6}>
            <Controller
              name="location.lat"
              control={control}
              rules={{ required: "Latitude is required." }}
              render={({ field }) => (
                <TextField
                  {...field}
                  required
                  fullWidth
                  label="Latitude"
                  size="small"
                  type="number"
                  error={!!errors.location?.lat}
                  helperText={errors.location?.lat?.message}
                />
              )}
            />
          </Grid>

          {/* Location - Longitude */}
          <Grid item xs={12} sm={6}>
            <Controller
              name="location.lon"
              control={control}
              rules={{ required: "Longitude is required." }}
              render={({ field }) => (
                <TextField
                  {...field}
                  required
                  fullWidth
                  label="Longitude"
                  size="small"
                  type="number"
                  error={!!errors.location?.lon}
                  helperText={errors.location?.lon?.message}
                />
              )}
            />
          </Grid>

          {/* Status */}
          <Grid item xs={12} sm={6}>
            <Controller
              name="status"
              control={control}
              rules={{ required: "Status is required." }}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  required
                  fullWidth
                  label="Status"
                  size="small"
                  error={!!errors.status}
                  helperText={errors.status?.message}
                >
                  <MenuItem value="On The Way">On The Way</MenuItem>
                  <MenuItem value="Delivered">Delivered</MenuItem>
                  <MenuItem value="Pending">Pending</MenuItem>
                  <MenuItem value="Canceled">Canceled</MenuItem>
                </TextField>
              )}
            />
          </Grid>

          {/* Is Active */}
          {trackingDetail && (
            <Grid item xs={6}>
              <Controller
                name="isActive"
                control={control}
                render={({ field }) => (
                  <AppSwitch {...field} label="Is Active?" />
                )}
              />
            </Grid>
          )}
        </Grid>
      </AppForm.Content>

      <AppForm.Actions>
        <Button onClick={() => navigate(-1)}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit(handleOnSubmit)}>
          {trackingDetail ? "Update Tracking" : "Add Tracking"}
        </Button>
      </AppForm.Actions>
    </AppForm>
  );
};

export default RealTimeTrackingForm;